import {Component, inject, Input, OnChanges} from '@angular/core';
import * as Highcharts from 'highcharts';
import {CommonModule} from '@angular/common';
import {HighchartsChartModule} from 'highcharts-angular';
import {NoWidgetDataComponent} from '../../../../components/my-brand-screen/no-widget-data/no-widget-data.component';
import {ChartData, WidgetValueField} from '../../../types/common';
import {Format, UtilService} from '../../../../services/util.service';
import {getContrastedColor} from '../../../../utils/colors';
import {LoaderComponent} from '../../loader/loader.component';

@Component({
  selector: 'app-pie-chart',
  imports: [CommonModule, HighchartsChartModule, NoWidgetDataComponent, LoaderComponent],
  templateUrl: './pie-chart.component.html',
  standalone: true,
  styleUrl: './pie-chart.scss'
})
export class PieChartComponent implements OnChanges {
  @Input() data: ChartData[] = [];
  @Input() colors: string[] = [];
  @Input() format: Format = '';
  @Input() valueField: WidgetValueField = '';
  @Input() isCompactMode = false;
  @Input() includeLegend = true;
  @Input() chartWidth: number | undefined;
  @Input() chartHeight: number | undefined;
  @Input() pieSize: number = 190;
  @Input() plotOptionsDistance: number = -23;
  @Input() loading = false;
  
  utilService = inject(UtilService);
  Highcharts: typeof Highcharts = Highcharts;
  chartOptions!: Highcharts.Options;

  ngOnChanges(): void {
    if (this.data.length) {
      this.chartOptions = {
        chart: {
          type: 'pie',
          width: this.chartWidth || null,
          height: this.chartHeight || null,
          borderWidth: 0,
        },
        title: {
          text: undefined,
        },
        tooltip: {
          enabled: this.isCompactMode,
          useHTML: true,
          backgroundColor: 'rgba(255,255,255,0)',
          borderWidth: 0,
          shadow: false,
          formatter: (function (utilService, format) {
            return function () {
              return `<div style="text-align: center;border-radius:4px;box-shadow:0px 0px 6px 0px rgba(0, 0, 0, 0.10);min-width:80px;padding: 4px 8px; margin: 3px; backgroundColor: white">
                        <div style="font-weight:400;font-size:12px;">${this.key}</div>
                        <div style="font-weight:600;font-size:14px;">${utilService.formatWidgetValue(this.y as number, format)}</div>
                      </div>`;
            };
          })(this.utilService, this.format),
          style: {
            fontFamily: 'Poppins',
            color: '#4E5258'
          },
        },
        plotOptions: this.getPlotOptions(),
        legend: {
          enabled: this.includeLegend,
          align: 'left',
          verticalAlign: 'bottom',
          layout: 'horizontal',
          symbolRadius: 6,
          itemStyle: {
            fontFamily: 'Poppins',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: '400',
            color: '#686D75',
            floating: true,
          },
        },
        credits: {
          enabled: false,
        },
        series: [
          {
            type: 'pie',
            data: this.data.map((item) => [item.name, item[this.valueField]]),
          },
        ],
      };
    }
  }

  private getDataLabelsPlotOptions(): Highcharts.SeriesPieDataLabelsOptionsObject {
    if (this.isCompactMode) {
      return {
        enabled: true,
        useHTML: true,
        formatter: (function (utilService, format) {
          return function (this: any) {
            const textColor = getContrastedColor(this.color as string);
            return `<div style="font-weight: 600; font-size: 14px; color: ${textColor}; text-align: center;">${utilService.formatWidgetValue(this.y as number, format, 0)}</div>`;
          };
        })(this.utilService, this.format),
        filter: {
          operator: '>',
          property: 'percentage',
          value: 4
        },
        style: {
          fontFamily: 'Poppins',
          color: '#4E5258'
        },
        distance: this.plotOptionsDistance,
        connectorWidth: 0,
        padding: 0
      }
    } else {
      return {
        padding: 4,
        enabled: true,
        useHTML: true,
        softConnector: false,
        distance: 65,
        connectorColor: '#BDC2CA',
        formatter: (function (utilService, format) {
          return function (this: any) {
            return `<div style="text-align: center;border-radius:4px;box-shadow:0px 0px 6px 0px rgba(0, 0, 0, 0.10);min-width:80px;padding: 4px 8px; max-width: 115px; margin: 3px;">
                        <div style="font-weight:400;font-size:12px; text-overflow: ellipsis; white-space: nowrap; overflow: hidden; ">${this.key}</div>
                        <div style="font-weight:400;font-size:16px;">${utilService.formatWidgetValue(this.y as number, format)}</div>
                      </div>`;
          };
        })(this.utilService, this.format),
        style: {
          fontFamily: 'Poppins',
          color: '#4E5258'
        }
      };
    }
  }

  private getPlotOptions(): Highcharts.PlotOptions {
    return {
      pie: {
        size: this.pieSize,
        center: ['50%', '135px'],
        innerSize: '55%',
        borderWidth: 0,
        dataLabels: this.getDataLabelsPlotOptions(),
        colors: this.colors,
        showInLegend: true,
        borderRadius: 0,
        states: {
          hover: {
            enabled: true,
            halo: {
              size: 0,
            }
          }
        }
      }
    };
  }
}
